import { useTranslation } from 'react-i18next';
import { Check, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { RouteSettingBoardCard } from '../../../types/route-setting-board';

/**
 * Says a climb is only a member's report so far — what the confirm button next
 * to it turns into the gym's own. Lives here so the card and the photo dialog
 * say it the same way.
 */
export function PendingBadge() {
  const { t } = useTranslation();

  return (
    <Badge
      variant="outline"
      className="shrink-0 border-amber-500/60 text-amber-600 dark:text-amber-400"
      data-testid="card-pending"
    >
      {t('gymAdmin.routeSettingBoard.pending')}
    </Badge>
  );
}

interface BoardCardActionsProps {
  card: RouteSettingBoardCard;
  /** Makes a member-reported climb official. Only drawn while it is pending. */
  onConfirm?: (card: RouteSettingBoardCard) => void;
  /** Takes the climb off the wall. */
  onDelete?: (card: RouteSettingBoardCard) => void;
  /**
   * `card` is the tight row at the foot of a board card; `dialog` is the photo's
   * footer, where there is room for buttons of a normal size.
   */
  variant?: 'card' | 'dialog';
}

/**
 * Confirm and delete, the two things a setter does to a climb without opening
 * it. Shared so the card and the photo dialog offer exactly the same pair —
 * whichever one a setter is looking at, the actions are where they expect.
 */
export function BoardCardActions({
  card,
  onConfirm,
  onDelete,
  variant = 'card',
}: BoardCardActionsProps) {
  const { t } = useTranslation();
  const compact = variant === 'card';
  const size = compact ? 'h-5 gap-1 px-1.5 text-[11px]' : 'gap-1.5';
  const icon = compact ? 'size-3' : 'size-4';
  // Both views can be on screen at once, so each names its own buttons.
  const testId = (action: string) => `${compact ? 'card' : 'dialog'}-${action}`;

  if (!onConfirm && !onDelete) return null;

  return (
    // Wrapping rather than overflowing: a language that spells these two longer
    // than the card is wide should push one onto a second line, not out of it.
    <div className="flex flex-wrap items-center justify-end gap-0.5">
      {/* Confirmed climbs have nothing left to confirm, so the button is only
          there while the climb is still a member's report. */}
      {onConfirm && card.official_status === 'pending' && (
        <Button
          variant={compact ? 'ghost' : 'outline'}
          size="sm"
          className={`${size} text-emerald-600 hover:bg-emerald-500/10 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300`}
          // The card is its own drag handle: a press on a button must not start
          // a drag.
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => onConfirm(card)}
          data-testid={testId('confirm')}
        >
          <Check className={icon} aria-hidden />
          {t('gymAdmin.routeSettingBoard.confirm')}
        </Button>
      )}

      {/* On the card the bin speaks for itself, and dropping its label leaves
          the width to the confirm — the one action that has to be read to be
          understood. Named for anyone not reading the icon. */}
      {onDelete && (
        <Button
          variant={compact ? 'ghost' : 'outline'}
          size="sm"
          className={`${compact ? 'size-5 p-0' : size} text-destructive hover:bg-destructive/10 hover:text-destructive`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => onDelete(card)}
          aria-label={t('gymAdmin.routeSettingBoard.delete')}
          title={t('gymAdmin.routeSettingBoard.delete')}
          data-testid={testId('delete')}
        >
          <Trash2 className={icon} aria-hidden />
          {!compact && t('gymAdmin.routeSettingBoard.delete')}
        </Button>
      )}
    </div>
  );
}
